import React, { useMemo } from 'react';
import type { WordCloudGroupedView, WordCloudGroupWord } from '../../core/types/payloads/poll.types';

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function tagWeight(word: WordCloudGroupWord, globalMax: number, size: 'md' | 'lg') {
  const freqRatio = word.globalCount / Math.max(globalMax, 1);
  const orderBoost = word.rankInResponse === 0 ? 1.08 : word.rankInResponse === 1 ? 1.02 : 1;
  const minPx = size === 'lg' ? 12 : 11;
  const maxPx = size === 'lg' ? 20 : 17;
  return clamp(minPx + freqRatio * (maxPx - minPx) * orderBoost, minPx, maxPx);
}

function tagOpacity(word: WordCloudGroupWord, globalMax: number) {
  const freqRatio = word.globalCount / Math.max(globalMax, 1);
  const orderBoost = word.rankInResponse === 0 ? 0.06 : 0;
  return clamp(0.72 + freqRatio * 0.28 + orderBoost, 0.72, 1);
}

function ParticipantCard({
  label,
  accent,
  words,
  globalMax,
  size,
  highlighted,
}: {
  label: string;
  accent: string;
  words: WordCloudGroupWord[];
  globalMax: number;
  size: 'md' | 'lg';
  highlighted?: boolean;
}) {
  return (
    <article
      className={`word-cloud-person ${highlighted ? 'word-cloud-person--highlight' : ''}`}
      style={{ ['--person-accent' as string]: accent }}
    >
      <header className="word-cloud-person__head">
        <span className="word-cloud-person__dot" aria-hidden />
        <span className="word-cloud-person__name">{label}</span>
        <span className="word-cloud-person__count">
          {words.length} palabra{words.length !== 1 ? 's' : ''}
        </span>
      </header>
      <div className="word-cloud-person__tags">
        {words.map((word) => (
          <span
            key={`${label}-${word.text}`}
            className="word-cloud-tag"
            style={{
              fontSize: tagWeight(word, globalMax, size),
              opacity: tagOpacity(word, globalMax),
              borderColor: `${accent}55`,
              background: `${accent}14`,
              color: accent,
            }}
            title={
              word.globalCount > 1
                ? `"${word.text}" · ${word.globalCount} menciones en la clase`
                : `"${word.text}" · solo en esta respuesta`
            }
          >
            {word.text}
            {word.globalCount > 1 && (
              <span className="word-cloud-tag__weight">×{word.globalCount}</span>
            )}
          </span>
        ))}
      </div>
    </article>
  );
}

export function WordCloudDisplay({
  data,
  size = 'md',
  highlightParticipantId,
}: {
  data: WordCloudGroupedView;
  size?: 'md' | 'lg';
  highlightParticipantId?: string;
}) {
  const { groups, consensus, totalPeople, totalMentions, globalMax } = data;

  const gridCols = useMemo(() => {
    if (size === 'lg') {
      if (groups.length <= 2) return 2;
      if (groups.length <= 4) return 2;
      if (groups.length <= 6) return 3;
      return 4;
    }
    if (groups.length <= 1) return 1;
    if (groups.length <= 4) return 2;
    return 3;
  }, [groups.length, size]);

  if (groups.length === 0) {
    return (
      <div
        className="flex w-full items-center justify-center py-16"
        style={{ minHeight: size === 'lg' ? 280 : 220, color: 'var(--plugin-muted)' }}
      >
        <p className="text-center text-base font-medium sm:text-lg">
          Esperando palabras de la clase…
        </p>
      </div>
    );
  }

  return (
    <div className="word-cloud-root w-full">
      <div className="word-cloud-by-person">
        <div
          className="word-cloud-person-grid"
          style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
        >
          {groups.map((group) => (
            <ParticipantCard
              key={group.participantId}
              label={group.label}
              accent={group.accent}
              words={group.words}
              globalMax={globalMax}
              size={size}
              highlighted={group.participantId === highlightParticipantId}
            />
          ))}
        </div>

        {consensus.length > 0 && (
          <section className="word-cloud-consensus" aria-label="Consenso de la clase">
            <p className="word-cloud-consensus__label">Consenso · mencionado por varios</p>
            <div className="word-cloud-consensus__tags">
              {consensus.map((item) => (
                <span
                  key={item.text}
                  className="word-cloud-consensus__tag"
                  title={`${item.peopleCount} personas · ${item.globalCount} menciones`}
                >
                  <span className="word-cloud-consensus__word">{item.text}</span>
                  <span className="word-cloud-consensus__meta">
                    {item.peopleCount} pers. · ×{item.globalCount}
                  </span>
                </span>
              ))}
            </div>
          </section>
        )}
      </div>

      <p
        className="mt-4 text-center text-[10px] font-bold uppercase tracking-[0.2em]"
        style={{ color: 'var(--plugin-muted)' }}
      >
        {totalPeople} respuesta{totalPeople !== 1 ? 's' : ''} · {totalMentions} menciones
        {consensus.length > 0 ? ` · ${consensus.length} en consenso` : ''}
      </p>
    </div>
  );
}
